import { useMemo } from "react";

import { useQuery } from "@tanstack/react-query";

import {
    compareModeEmptyStateWebsites,
    emptyStateSuggestedWebsites,
} from "app/pages/website-analysis/website-structure/pages/LineOfBusiness/consts";
import { DefaultFetchService } from "app/services/fetchService";
import { useDomainsFavicons } from "app/services/sitesService/hooks/useDomainsFavicons";
import { proNavigator } from "single-spa/react-app/ProNavigator";

const fetchService = DefaultFetchService.getInstance();

type LobEntry = { Site: string; Vertical: string; FavIcon: string };
export type LobItem = { name: string; icon: string };

export type LobData = {
    showLob: boolean;
    lobSites: LobItem[];
    isLobLoading: boolean;
    topAnalyzedLOBWebsites: LobItem[];
    compareModeWebsites: LobItem[];
};

type UseLobSitesOptions = {
    shouldFetch?: boolean;
};

const useLobSites = (options: UseLobSitesOptions = {}): LobData => {
    const { shouldFetch = false } = options;
    const { selectedTab, key } = proNavigator.getParams();
    const shouldFetchData = selectedTab === "LOB" || shouldFetch;

    const { data = [], isLoading: isLobLoading } = useQuery<LobEntry[]>({
        queryKey: ["lob-sites"],
        queryFn: () => fetchService.get<LobEntry[]>("api/websiteContent/lob/metadata/lobSites").then((r) => r ?? []),
        enabled: shouldFetchData,
        staleTime: 5 * 60 * 1000,
    });

    const lobSites = useMemo<LobItem[]>(() => {
        return data.map(({ Site, FavIcon }) => ({
            name: Site,
            icon: FavIcon,
        }));
    }, [data]);

    const { data: topAnalyzedFaviconsList = [] } = useDomainsFavicons(emptyStateSuggestedWebsites, {
        enabled: shouldFetchData,
    });

    const { data: compareModeFaviconsList = [] } = useDomainsFavicons(compareModeEmptyStateWebsites, {
        enabled: shouldFetchData,
    });

    const topAnalyzedLOBWebsites = useMemo<LobItem[]>(() => {
        return topAnalyzedFaviconsList.map(({ Domain, Favicon }) => ({ name: Domain, icon: Favicon }));
    }, [topAnalyzedFaviconsList]);

    const compareModeWebsitesData = useMemo<LobItem[]>(() => {
        return compareModeFaviconsList.map(({ Domain, Favicon }) => ({ name: Domain, icon: Favicon }));
    }, [compareModeFaviconsList]);

    const showLob = shouldFetchData && key && lobSites.length > 0 && topAnalyzedLOBWebsites.length > 0;

    return {
        showLob,
        lobSites,
        isLobLoading,
        topAnalyzedLOBWebsites,
        compareModeWebsites: compareModeWebsitesData,
    };
};

export default useLobSites;
